"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { hindiToggleHref } from "@/i18n/hindi-twin";
import { detectLocale, getLocalePath, locales } from "@/i18n/locales";
import { LANGUAGE_SWITCH, type LanguageSwitchCopy } from "@/lib/chrome-locale";
import { goneFamilyHubPath, legacyCrawlCanonicalPath } from "@/lib/crawl-policy";

/**
 * Language toggle — honest contract (2026-07-08 rebuild):
 *   exact twin exists      → switch IN PLACE to the counterpart page
 *   only the topic hub     → switch to the hub (nested en page, hi hub only;
 *                            hi page whose en twin answers 410 → en family hub)
 *   no twin at all         → the chip does NOT render (never an unrelated page)
 *
 * The old fallback sent every twin-less page to /hi/aaj-ka-rashifal/ — the
 * "I pressed Hindi and some other page loaded" bug. Twin existence is DERIVED
 * from the filesystem scan (locale-twins.generated.ts) + the D1-served festival
 * list, so "twin exists" can no longer drift from reality. The chip is a plain
 * <a>, so it is also the crawlable link from each English page to its twin.
 *
 * `compact` is the 44x44 chip in the phone header bar (xb-03, 2026-10-07): the
 * switch used to be reachable only at the bottom of the hamburger menu, two
 * taps deep, while the in-form "हिंदी" tab beside it changed nothing. Labels
 * come from `LANGUAGE_SWITCH`, which the static header renders too. Both chips
 * carry `lang`: each label is written in the language it switches to.
 */
export default function LanguageToggle({ compact = false }: { compact?: boolean }) {
  const pathname = usePathname();
  const activeLocale = detectLocale(pathname);
  // Strip the locale prefix → [slug, ...subPath]. The slug (first segment) may
  // carry a per-locale alias; deeper segments (e.g. the planet on
  // /lal-kitab-remedies/<planet>/) are locale-invariant when the counterpart
  // side actually serves them.
  const segments = pathname.split("/").filter(Boolean);
  const pathSegments = segments[0] === "hi" ? segments.slice(1) : segments;
  const slug = pathSegments[0] ?? "";
  const subPath = pathSegments.slice(1).join("/");

  const href = twinHref(pathname, activeLocale, slug, subPath);
  // No twin: no chip at all, never a link to an unrelated page.
  if (!href) return null;
  const copy = LANGUAGE_SWITCH[activeLocale === "hi" ? "hi" : "en"];
  if (compact) return <CompactChip href={href} copy={copy} />;
  return (
    <div className="flex gap-1.5">
      <ToggleChip href={href} copy={copy} />
    </div>
  );
}

/** The other language's page for this route, or null when it has none. */
function twinHref(
  pathname: string,
  activeLocale: string,
  slug: string,
  subPath: string
): string | null {
  const target = locales.find((locale) => locale !== activeLocale);
  if (target === "hi") {
    // The exact nested twin when a Hindi page serves it
    // (/calendar/festival/holi/ → /hi/calendar/festival/holi/), else the
    // section's Hindi hub, else nothing rather than an unrelated page.
    return hindiToggleHref(pathname);
  }
  // → en: a hi page localizes an en surface, so keep the nested sub-path
  // (this kills the lal-kitab hub 301 that trips the live-4xx audit). The
  // exception is a hand-rewritten Hindi page kept inside a removed family
  // (gonePathExemptPaths: the /hi/graha placement pages, the Hindi antardasha
  // leaves): its English counterpart answers 410 since 2026-09-23, so the
  // switch goes to the family hub that 410 page links (/graha/, /mahadasha/).
  // The live-4xx audit failed the 2026-10-15 deploy that put the 7 graha pages
  // on the app for exactly this link.
  const base = getLocalePath(slug, "en");
  const exact = subPath ? `${base.replace(/\/$/, "")}/${subPath}/` : base;
  return goneFamilyHubPath(exact) ?? exact;
}

function CompactChip({ href, copy }: { href: string; copy: LanguageSwitchCopy }) {
  return (
    <Link
      href={legacyCrawlCanonicalPath(href) ?? href}
      prefetch={false}
      hrefLang={copy.target}
      lang={copy.target}
      title={copy.title}
      aria-label={copy.title}
      data-analytics-placement="header_language"
      className="inline-flex size-11 items-center justify-center rounded-lg border border-border-default text-caption font-semibold text-text-secondary transition hover:bg-surface-brand-subtle"
    >
      {copy.compactLabel}
    </Link>
  );
}

function ToggleChip({ href, copy }: { href: string; copy: LanguageSwitchCopy }) {
  return (
    <Link
      href={legacyCrawlCanonicalPath(href) ?? href}
      prefetch={false}
      hrefLang={copy.target}
      lang={copy.target}
      data-analytics-placement="header_menu_language"
      className="inline-flex min-h-11 items-center rounded-md border border-border-default px-2.5 py-1.5 text-xs font-semibold text-text-secondary transition hover:bg-surface-brand-subtle"
      title={copy.title}
    >
      {copy.label}
    </Link>
  );
}
